import * as React from 'react';

import {
  ExpressionBinding,
  ExpressionEditor,
  ExpressionEditorImplementationContext,
  extendExpressionEditorActionAtPath,
  getBindingLabel,
  useExpressionEditorImplementationContext,
  type ExpressionBindMetaPayload,
  type ExpressionEditorActionPayload,
  type ExpressionEditorDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  CustomControls,
  useLabelItems,
  type CustomControlsType,
} from '@packages/systems/ExpressionEditor-custom-controls';
import {SettingsPanelPropertyLabel} from '@packages/systems/ui/shared-ui/SettingsPanelPropertyLabel/SettingsPanelPropertyLabel';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';
import {
  CssPreviewIcon,
  Divider,
  HelpCircleIcon,
  HStack,
  Tag,
  Text,
  Tooltip,
  View,
} from '@packages/systems/spring';
/**
 * TODO: Replace DeprecatedPopoverBridge with Spring Popover once there is a
 * reliable way to test nested popovers in Cypress headless mode.
 *
 * Spring Popover's `useDismiss` (floating-ui) listens for `pointerdown` to
 * detect outside clicks. Legacy overlays register via `useRegisterLegacyOverlay`
 * (`useEffect`, runs AFTER paint). If Cypress dispatches `pointerdown` before
 * the effect fires, the parent Spring Popover closes unexpectedly.
 *
 * The native ExpressionEditor also uses DeprecatedPopoverBridge for its TList
 * item popovers, so we keep this custom list implementation consistent with
 * that approach.
 *
 * @see packages/systems/spring/hooks/useFloating/useFloatingTree.ts — useLegacyOverlayCoordinator
 * @see packages/systems/spring/components/Popover/usePopover.ts — useDismiss config
 */
import {
  CLOSED_STATE,
  DeprecatedPopoverBridge,
  OPENED_STATE,
} from '@packages/systems/design-system/PopoverBridge';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import PropertyList, {
  PropertyListAddButton,
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {
  ERecord,
  EText,
  TList,
  TTypeConstructor,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {deconstructAppend} from '@packages/utilities/append';
import {
  type CheckerState,
  ExpressionPath,
  Expressions,
  type MetaAttributes,
  TypeCheckerState,
  TypeImplementation,
  checkType,
  getMeta,
  getSystem,
  withMeta,
  withSystem,
} from '@packages/systems/wfdl';
import {
  iconName,
  pluginName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {createSelector} from 'reselect';
import {
  ContextualExpressions,
  getAnn,
  stripAll,
  stripAnn,
  updateAnn,
  type ContextualExpression,
} from '@packages/utilities/contextual-expression';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  selectIsOnPrimaryLocale,
  useLocalizationSelector,
} from '@packages/systems/localization';
import {getLocalizationInfoFromExpression} from '@packages/domains/localization/dom/model';
import {selectExternalizedLocalizationBindingsEnabled} from '@packages/domains/localization/dom/flux';

// ==========================================================================
// Names
// ==========================================================================
const AttributesTypeConstructor = TTypeConstructor(
  qualify(pluginName('Basic'), typeName('Attributes'))
);
const CustomAttributeTypeConstructor = TTypeConstructor(
  qualify(pluginName('Basic'), typeName('CustomAttribute'))
);

const EMPTY_ITEMS: ContextualExpression[] = [];

// ==========================================================================
// Helpers
// ==========================================================================
/**
 * Deconstructs an attributes expression into its constituent parts:
 * the static list, the optional bound list, and the resolved static items.
 *
 * Localization is already substituted into the Append args, so a static
 * second arg is always treated as the localized list:
 * - Append(plain, localized, binding)
 * - Append(plain, binding)
 */
interface AppendExpressionParts {
  plain: ContextualExpression | null;
  localized: ContextualExpression | null;
  binding: ContextualExpression | null;
}
function defineAppendExpressionParts(
  parts: ContextualExpression[]
): AppendExpressionParts {
  const [first, second, third] = parts;

  if (third) {
    return {
      plain: first ?? null,
      localized: second ?? null,
      binding: third,
    };
  }

  if (second) {
    if (Expressions.isBinding(stripAll(second))) {
      return {
        plain: first ?? null,
        localized: null,
        binding: second,
      };
    }

    return {
      plain: first ?? null,
      localized: second,
      binding: null,
    };
  }

  if (first && Expressions.isBinding(stripAll(first))) {
    return {
      plain: null,
      localized: null,
      binding: first,
    };
  }

  return {
    plain: first ?? null,
    localized: null,
    binding: null,
  };
}

export const AppendShape = {
  Plain: 'plain',
  BareBinding: 'bare-binding',
  ComposedStaticLoc: 'composed-static-loc',
  ComposedStaticBinding: 'composed-static-binding',
  ComposedStaticLocBinding: 'composed-static-loc-binding',
} as const;

type AppendShapeType = (typeof AppendShape)[keyof typeof AppendShape];

function defineShape(
  hasAppend: boolean,
  parts: AppendExpressionParts
): AppendShapeType {
  const isBareBinding =
    !hasAppend &&
    parts.binding != null &&
    Expressions.isBinding(stripAll(parts.binding));

  if (isBareBinding) return AppendShape.BareBinding;
  if (!hasAppend) return AppendShape.Plain;

  if (parts.localized != null && parts.binding != null) {
    return AppendShape.ComposedStaticLocBinding;
  }
  if (parts.binding != null) return AppendShape.ComposedStaticBinding;
  if (parts.localized != null) return AppendShape.ComposedStaticLoc;
  return AppendShape.Plain;
}

export const isShape = (
  currentShape: AppendShapeType,
  shape: 'composed' | 'localization' | 'binding'
) => {
  if (shape === 'composed') {
    return (
      currentShape === AppendShape.ComposedStaticLoc ||
      currentShape === AppendShape.ComposedStaticBinding ||
      currentShape === AppendShape.ComposedStaticLocBinding
    );
  }
  if (shape === 'binding') {
    return (
      currentShape === AppendShape.ComposedStaticBinding ||
      currentShape === AppendShape.ComposedStaticLocBinding
    );
  }

  if (shape === 'localization') {
    return (
      currentShape === AppendShape.ComposedStaticLoc ||
      currentShape === AppendShape.ComposedStaticLocBinding
    );
  }

  return false;
};

/**
 * Panel expr is usually already substituted. Reset only when there is a
 * localization arm with a non-empty list (or a raw loc Select that failed to
 * substitute). Empty substituted List([]) means "no localization" → no Reset.
 */
export function hasNonEmptyLocalizedAttributesArm(
  expr: ContextualExpression
): boolean {
  const parts = defineAppendExpressionParts(deconstructAppend(expr));
  if (parts.localized == null) return false;

  const raw = stripAll(parts.localized);
  const list = Expressions.getList(raw);
  if (list) return list.length > 0;

  return Boolean(getLocalizationInfoFromExpression(raw));
}

type AttributesPropertyListItem = {
  id: string;
  source: 'static' | 'localized';
  index: number;
  item: ContextualExpression;
  isDeletable: boolean;
};

function attributeNameMergeKey(item: ContextualExpression): string | null {
  const name = Expressions.getRecord(stripAll(item))?.name;
  if (!name) return null;
  if (Expressions.isBinding(name)) return `binding:${JSON.stringify(name)}`;
  return Expressions.getText(name) || null;
}

function definePropertyListItems(
  staticList: ContextualExpression | null,
  localizedList: ContextualExpression | null,
  isDeletable: boolean
): AttributesPropertyListItem[] {
  const staticItems =
    (staticList && ContextualExpressions.getList(stripAnn(staticList))) ||
    EMPTY_ITEMS;

  const localizedItems =
    (localizedList && ContextualExpressions.getList(stripAnn(localizedList))) ||
    EMPTY_ITEMS;

  const rows = new Map<string, AttributesPropertyListItem>();

  staticItems.forEach((item, index) => {
    const nameKey = attributeNameMergeKey(item);
    const key = nameKey ?? `static:${index}`;
    rows.set(key, {
      id: String(index),
      item,
      source: 'static',
      index,
      isDeletable,
    });
  });

  localizedItems.forEach((item, index) => {
    const nameKey = attributeNameMergeKey(item);
    const existing = nameKey != null ? rows.get(nameKey) : undefined;
    const key = nameKey ?? `loc:${index}`;

    rows.set(key, {
      item,
      id: `loc-${index}`,
      source: 'localized',
      index: existing ? existing.index : rows.size,
      isDeletable: true,
    });
  });

  return Array.from(rows.values());
}

export const parseAttributesExpr = (
  expr: ContextualExpression,
  isDeletable: boolean
) => {
  const parts = deconstructAppend(expr);
  const appendParts = defineAppendExpressionParts(parts);
  const shape = defineShape(parts.length > 1, appendParts);

  return {
    staticListExpr: appendParts.plain,
    boundListExpr: appendParts.binding,
    shape,
    propertyListItems: definePropertyListItems(
      appendParts.plain,
      appendParts.localized,
      isDeletable
    ),
  };
};

function itemPath(
  listItem: AttributesPropertyListItem,
  shape: AppendShapeType
): ExpressionPath {
  const {id, source} = listItem;

  if (source === 'localized') {
    const locIndex = Number(id.slice('loc-'.length));

    if (isShape(shape, 'localization') && isShape(shape, 'binding')) {
      return [
        {in: 'Call', at: 'arg'},
        {in: 'Call', at: 'fun'},
        {in: 'Call', at: 'arg'},
        {in: 'List', index: locIndex},
      ];
    }

    if (isShape(shape, 'localization')) {
      return [
        {in: 'Call', at: 'arg'},
        {in: 'List', index: locIndex},
      ];
    }
  }

  const staticIndex = Number(id);
  if (isShape(shape, 'composed')) {
    return [
      {in: 'Call', at: 'fun'},
      {in: 'Call', at: 'arg'},
      {in: 'List', index: staticIndex},
    ];
  }
  return [{in: 'List', index: staticIndex}];
}

/**
 * Wraps a contextual expression's type annotation so that its system metadata
 * has `isBindable: true` and uses the `Attributes` type constructor. This
 * enables the ExpressionBinding UI to show the binding controls.
 */
const wrapExpr = (expr: ContextualExpression): ContextualExpression =>
  updateAnn((ann) => {
    if (!ann.type) return ann;
    return {
      ...ann,
      type: withSystem(
        {...getSystem(ann.type), isBindable: true},
        AttributesTypeConstructor
      ),
    };
  }, expr);

/**
 * Prepares a single CustomAttribute item expression for the ExpressionEditor.
 * Returns a `[TypeCheckerState, ContextualExpression]` tuple ready to be
 * supplied to `TypeCheckerState.Provider`.
 *
 * - Runs `checkType` to fill in type information for the Record's nested fields
 * - Sets `isDefault: true` on every annotation so the blue override-reset
 *   indicator only appears at the top-level Attributes field, not on
 *   individual Name / Value pairs.
 * - Sets `isBindable: true` on every annotation so both Name and Value
 *   sub-fields expose the binding pill.
 * - Forwards the parent Attributes list's meta down to every nested annotation
 *   so per-item renderers can read call-site flags via `getMeta(...)`.
 */
const prepareCustomAttributeItem = (
  itemExpr: Expression,
  rootCheckerState: CheckerState,
  parentMeta: MetaAttributes | null,
  isBindable: boolean
): [CheckerState, ContextualExpression] => {
  const [itemCheckerState, typeCheckedExpr] = checkType(
    itemExpr,
    CustomAttributeTypeConstructor
  )(rootCheckerState);

  const enrichedExpr = ContextualExpressions.everywhere(
    (expr: ContextualExpression): ContextualExpression =>
      updateAnn((ann) => {
        if (!ann.type) return ann;
        const withSystemUpdated = withSystem(
          {...getSystem(ann.type), isDefault: true, isBindable},
          ann.type
        );
        return {
          ...ann,
          type: parentMeta
            ? withMeta({...getMeta(ann.type), ...parentMeta}, withSystemUpdated)
            : withSystemUpdated,
        };
      }, expr)
  )(typeCheckedExpr);

  return [itemCheckerState, enrichedExpr];
};

const hasEmptyAttributeName = (itemExpr: Expression): boolean => {
  const record = Expressions.getRecord(itemExpr);
  if (!record?.name) return true;
  if (Expressions.isBinding(record.name)) return false;
  return Expressions.getText(record.name) === '';
};

/**
 * Wraps each label-condition handler so it receives the sub-expression with an
 * annotation path rooted at element data rather than at the item. This keeps
 * `ExpressionBinding` working (it uses the original item-relative path against
 * the item-scoped `TypeCheckerState`) while letting conditions like
 * `localizationLabelConditionCheck` resolve primary vs localized settings from
 * the element data. Only the label condition sees the adjusted path.
 */
const wrapLabelControlsWithAbsolutePath = (
  labelControls: CustomControlsType['LabelControls'],
  itemAbsolutePath: ExpressionPath
): CustomControlsType['LabelControls'] =>
  labelControls.map((labelItem) => ({
    condition: (subExpr, labelDispatch, eeContext) => {
      const adjustedExpr = updateAnn(
        (ann) => ({
          ...ann,
          env: {
            ...ann.env,
            expressionPath: [...itemAbsolutePath, ...ann.env.expressionPath],
          },
        }),
        subExpr
      );
      return labelItem.condition(adjustedExpr, labelDispatch, eeContext);
    },
  }));

// ==========================================================================
// Styled Components
// ==========================================================================
const StyledDivider = emotionStyled(Divider)({
  width: '95%',
  margin: 'auto',
});

const AttributeEditorContainer = emotionStyled('div')({
  width: rightSidebarWidth,
  padding: tokens.space[2],
});

// ==========================================================================
// Components
// ==========================================================================
/**
 * Top-level "Attributes" label. Runs the surrounding `LabelControls` against
 * the whole Attributes expression so affordances like the localization
 * "Reset to primary locale" button render here — the outer `PropertyLabel`
 * is hidden by `labelVariant: 'hidden'` in `AttributeSettingsExpressionEditor`.
 *
 * With externalized bindings, Reset only appears when Append has a non-empty
 * localization arm (substituted List or unresolved loc Select).
 */
const AttributesLabel = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}) => {
  const {context} = useExpressionEditorImplementationContext().env;
  const parentCustomControls = React.useContext(CustomControls);
  const isLocalizationExternalizedBindingEnabled = useDesignerSelector(
    selectExternalizedBindingsEnabled
  );

  const showLocalizationReset =
    !isLocalizationExternalizedBindingEnabled ||
    hasNonEmptyLocalizedAttributesArm(expr);

  const labelControls = React.useMemo(
    () =>
      showLocalizationReset
        ? parentCustomControls
        : {...parentCustomControls, LabelControls: []},
    [showLocalizationReset, parentCustomControls]
  );

  return (
    <CustomControls.Provider value={labelControls}>
      <AttributesLabelItems expr={expr} dispatch={dispatch} context={context} />
    </CustomControls.Provider>
  );
};

const AttributesLabelItems = ({
  expr,
  dispatch,
  context,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  context: TypeImplementation.DataTypeEnvironmentContext;
}) => {
  const items = useLabelItems(expr, dispatch, context);

  return (
    <SettingsPanelPropertyLabel
      items={items}
      data-automation-id="Type--Label_Custom attributes"
    >
      <Text colorVariant="secondary" flexShrink={0}>
        Attributes
      </Text>
    </SettingsPanelPropertyLabel>
  );
};

const Footer = (props: {bindingLabel: string}) => {
  const {bindingLabel} = props;

  return (
    <View role="status" aria-live="polite">
      <StyledDivider elevation={1} />
      <HStack
        data-automation-id="attributes-bound-list-footer"
        spacing={1}
        padding="$2"
        align="center"
      >
        <Text colorVariant="secondary">+ items in</Text>
        <Tag
          data-automation-id="attributes-bound-prop-label"
          colorVariant="purple"
          density="compact"
          prefix={CssPreviewIcon}
          maxWidth={130}
        >
          {bindingLabel}
        </Tag>
        <Tooltip
          maxWidth={190}
          data-automation-id="attributes-override-tooltip"
          aria-label={`Duplicate attributes in ${bindingLabel} will override static values.`}
          label={
            <Text>
              Duplicate attributes in{' '}
              <Text as="span" colorVariant="purple">
                {bindingLabel}
              </Text>{' '}
              will override static values.
            </Text>
          }
        >
          <HelpCircleIcon
            colorVariant="secondary"
            data-automation-id="attributes-override-tooltip-trigger"
          />
        </Tooltip>
      </HStack>
    </View>
  );
};

type PendingWrite =
  | {
      kind: 'localization-static-change';
      updates: {
        key: ExpressionPath;
        expr: Expression;
      }[];
    }
  | {
      kind: 'localization-localized-change';
      index: number;
      updates: {
        key: ExpressionPath;
        expr: Expression;
      }[];
    };

const selectExternalizedBindingsEnabled = createSelector(
  (state: DesignerStores) => state.DomLocalizationStore,
  selectExternalizedLocalizationBindingsEnabled
);

const AttributesTypeRender = ({
  expr,
  dispatch,
}: {
  dispatch: ExpressionEditorDispatch;
  expr: ContextualExpression;
}) => {
  const outerPath = getAnn(expr).env.expressionPath;
  const isOnPrimaryLocale = useLocalizationSelector(selectIsOnPrimaryLocale);
  const isLocalizationExternalizedBindingEnabled = useDesignerSelector(
    selectExternalizedBindingsEnabled
  );

  const {staticListExpr, boundListExpr, shape, propertyListItems} =
    React.useMemo(() => {
      const isDeletable = isLocalizationExternalizedBindingEnabled
        ? isOnPrimaryLocale
        : true;
      return parseAttributesExpr(expr, isDeletable);
    }, [expr, isLocalizationExternalizedBindingEnabled, isOnPrimaryLocale]);

  const [itemIndex, setItemIndex] = React.useState(-1);
  // Tracks whether the currently-open attribute editor has an empty Name input.
  // Only one popover can be open at a time, so a single boolean is sufficient.
  const editedNameIsEmptyRef = React.useRef(false);

  const pendingWrite = React.useRef<PendingWrite | null>(null);
  // Open row for unmount flush — canvas dismiss skips popover onChange(close).
  const openItemRef = React.useRef<AttributesPropertyListItem | null>(null);

  const onBindingChange = React.useCallback(
    (updatedBinding: Expression | null, meta: ExpressionBindMetaPayload) => {
      dispatch({
        key: emptyArray,
        type: 'EXPRESSION_ASSOC_LIST_BINDING_CHANGED',
        payload: {
          binding: updatedBinding,
        },
        meta,
      });
    },
    [dispatch]
  );

  const onAddItem = React.useCallback(
    (newItemExpr: Expression) => {
      setItemIndex(propertyListItems.length);
      dispatch({
        type: 'EXPRESSION_ASSOC_LIST_ITEM_ADDED',
        key: emptyArray,
        payload: {
          item: newItemExpr,
        },
      });
    },
    [dispatch, propertyListItems.length]
  );

  const onItemDeleted = React.useCallback(
    (id: string) => {
      const index = id.startsWith('loc-')
        ? Number(id.slice('loc-'.length))
        : Number(id);

      dispatch({
        type: 'EXPRESSION_ASSOC_LIST_ITEM_REMOVED',
        key: emptyArray,
        payload: {index},
      });

      return;
    },
    [dispatch]
  );

  const processPendingWrite = React.useCallback(
    (listItem: AttributesPropertyListItem): Expression => {
      let nextExpr = stripAll(listItem.item);
      const pending = pendingWrite.current;
      if (!pending) return nextExpr;

      nextExpr = pending.updates.reduce(
        (acc, update) => Expressions.setIn(acc, update.key, update.expr),
        nextExpr
      );

      switch (pending.kind) {
        case 'localization-static-change': {
          if (!hasEmptyAttributeName(nextExpr)) {
            onAddItem(nextExpr);
          }
          break;
        }
        case 'localization-localized-change': {
          dispatch({
            type: 'EXPRESSION_ASSOC_LIST_ITEM_UPDATED',
            key: emptyArray,
            payload: {
              index: pending.index,
              newExpression: nextExpr,
            },
          });
          break;
        }
      }

      pendingWrite.current = null;
      return nextExpr;
    },
    [dispatch, onAddItem]
  );

  // Refs for useEffect cleanup — these functions can't be destructured
  const flushPendingWriteRef = React.useRef(processPendingWrite);
  flushPendingWriteRef.current = processPendingWrite;
  const onItemDeletedRef = React.useRef(onItemDeleted);
  onItemDeletedRef.current = onItemDeleted;

  // Unmount
  React.useEffect(() => {
    return () => {
      const listItem = openItemRef.current;
      if (!listItem || !pendingWrite.current) return;
      const nextExpr = flushPendingWriteRef.current(listItem);
      const shouldDelete =
        editedNameIsEmptyRef.current || hasEmptyAttributeName(nextExpr);
      editedNameIsEmptyRef.current = false;
      if (listItem.isDeletable && shouldDelete) {
        onItemDeletedRef.current(listItem.id);
      }
    };
  }, []);

  const onItemEdited = React.useCallback(
    (listItem: AttributesPropertyListItem) => {
      const defaultDispatch = extendExpressionEditorActionAtPath(dispatch)(
        itemPath(listItem, shape)
      );

      return (action: ExpressionEditorActionPayload) => {
        if (!isLocalizationExternalizedBindingEnabled)
          return defaultDispatch(action);
        if (isOnPrimaryLocale) return defaultDispatch(action);
        if (action.type !== 'EXPRESSION_ACTION') return defaultDispatch(action);

        const updates = pendingWrite.current?.updates ?? [];

        if (listItem.source === 'localized') {
          const locIndex = Number(listItem.id.slice('loc-'.length));

          openItemRef.current = listItem;
          pendingWrite.current = {
            kind: 'localization-localized-change',
            index: locIndex,
            updates: [
              ...updates,
              {
                key: action.key,
                expr: action.payload.newExpression,
              },
            ],
          };

          return;
        }

        if (listItem.source === 'static') {
          openItemRef.current = listItem;
          pendingWrite.current = {
            kind: 'localization-static-change',
            updates: [
              ...updates,
              {
                key: action.key,
                expr: action.payload.newExpression,
              },
            ],
          };

          return;
        }
      };
    },
    [
      isLocalizationExternalizedBindingEnabled,
      isOnPrimaryLocale,
      shape,
      dispatch,
    ]
  );

  const onItemDropped = React.useCallback(
    ({itemId, dropTargetId}: {itemId: string; dropTargetId: string}) => {
      const canReorderAttributes =
        !isLocalizationExternalizedBindingEnabled || isOnPrimaryLocale;
      if (!canReorderAttributes) return;

      const parseRowIndex = (id: string) =>
        Number(id.startsWith('loc-') ? id.slice('loc-'.length) : id);
      const fromIndex = parseRowIndex(itemId);
      const toIndex = parseRowIndex(dropTargetId);
      if (Number.isNaN(fromIndex) || Number.isNaN(toIndex)) return;

      // Close any open item popover before reordering so the popover's stored
      // index doesn't refer to the wrong row after the list rearranges.
      setItemIndex(-1);
      dispatch({
        type: 'EXPRESSION_ASSOC_LIST_ITEM_MOVED',
        key: emptyArray,
        payload: {fromIndex, toIndex},
      });
    },
    [dispatch, isLocalizationExternalizedBindingEnabled, isOnPrimaryLocale]
  );

  const onAttributeNameEmptyChange = React.useCallback((isEmpty: boolean) => {
    editedNameIsEmptyRef.current = isEmpty;
  }, []);

  const [rootCheckerState] = React.useContext(TypeCheckerState);
  const parentCustomControls = React.useContext(CustomControls);
  const {env} = useExpressionEditorImplementationContext();
  // The outer `env.context` is `'override'` when the Attributes editor is
  // rendered for a component-instance prop override. That treatment is meant
  // for top-level instance prop rows — inside the per-item popover it would
  // stack the Name/Value sub-fields vertically and inject a type icon. Reset
  // to `'default'` for the popover body so the sub-fields render with the
  // same compact grid layout as the element-level xattr editor. Mirrors the
  // pattern in `packages/systems/plugins/Conditionals/RuleConditions.tsx`.
  const popoverEnv = React.useMemo(
    () => ({...env, context: 'default' as const}),
    [env]
  );

  const parentExprType = getAnn(expr).type;
  const parentMeta = parentExprType ? getMeta(parentExprType) : null;

  const onItemPopoverChange = React.useCallback(
    (listItem: AttributesPropertyListItem) => (newState: {open: boolean}) => {
      if (newState.open) {
        openItemRef.current = listItem;
        setItemIndex(listItem.index);
        return;
      }

      const nextExpr = processPendingWrite(listItem);
      openItemRef.current = null;

      const shouldDelete =
        editedNameIsEmptyRef.current || hasEmptyAttributeName(nextExpr);
      editedNameIsEmptyRef.current = false;
      if (listItem.isDeletable && shouldDelete) onItemDeleted(listItem.id);

      setItemIndex(-1);
    },
    [processPendingWrite, onItemDeleted]
  );

  const renderItem = (listItem: AttributesPropertyListItem) => {
    const itemExpr = stripAll(listItem.item);
    const [itemCheckerState, typeCheckedExpr] = prepareCustomAttributeItem(
      itemExpr,
      rootCheckerState,
      parentMeta,
      true
    );

    const itemAbsolutePath: ExpressionPath = [
      ...outerPath,
      ...itemPath(listItem, shape),
    ];
    // Externalized Append paths go through Call segments that do not exist on
    // primary Dom. Wrapping LabelControls onto Name/Value then falsely shows
    // "Reset to primary" and DOM-reset deletes through the Call path, wiping
    // xattr. Reset belongs only on the top-level Attributes label.
    const wrappedLabelControls = isLocalizationExternalizedBindingEnabled
      ? []
      : wrapLabelControlsWithAbsolutePath(
          parentCustomControls.LabelControls,
          itemAbsolutePath
        );

    // The Alias render function type-checks each item individually,
    // so downstream consumers (e.g. property name suggestion) need
    // the individual Record expression, not the full List/Append.
    const customControls = {
      ...parentCustomControls,
      LabelControls: wrappedLabelControls,
      TopLevelOptionsContext: {
        ...parentCustomControls.TopLevelOptionsContext,
        attributeExpr: itemExpr,
        onAttributeNameEmptyChange,
      },
    };

    return (
      <DeprecatedPopoverBridge
        key={listItem.id}
        state={listItem.index === itemIndex ? OPENED_STATE : CLOSED_STATE}
        onChange={onItemPopoverChange(listItem)}
        showArrow={false}
        showMask="TRANSPARENT"
        renderPopoverContent={() => (
          <AttributeEditorContainer
            id={`attribute-editor-${listItem.index}`}
            data-automation-id={`attribute-editor-${listItem.index}`}
            aria-label={`Edit attribute ${listItem.index + 1}`}
          >
            <CustomControls.Provider value={customControls}>
              <TypeCheckerState.Provider
                value={[itemCheckerState, typeCheckedExpr]}
              >
                <ExpressionEditorImplementationContext.Provider
                  value={{env: popoverEnv}}
                >
                  <ExpressionEditor
                    dispatch={onItemEdited(listItem)}
                    expression={typeCheckedExpr}
                  />
                </ExpressionEditorImplementationContext.Provider>
              </TypeCheckerState.Provider>
            </CustomControls.Provider>
          </AttributeEditorContainer>
        )}
      >
        {({openPopover, Anchor}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <PropertyListItem
                _ref={ref}
                key={listItem.id}
                id={listItem.id}
                aria-expanded={listItem.index === itemIndex}
                aria-haspopup="dialog"
                aria-controls={`attribute-editor-${listItem.index}`}
                editable
                deleteDisabled={!listItem.isDeletable}
                label={
                  <DataTypeSummary
                    dataType={CustomAttributeTypeConstructor}
                    context={typeCheckedExpr}
                  />
                }
                onClick={openPopover}
                onEditButtonClicked={openPopover}
              />
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    );
  };

  const renderFooter = React.useCallback(() => {
    const hasItems = propertyListItems.length > 0;
    if (!boundListExpr || !hasItems) {
      return null;
    }

    const bindingLabel = getBindingLabel(
      getAnn(boundListExpr).env.context.values,
      stripAll(boundListExpr)
    );

    if (!bindingLabel) {
      return null;
    }

    return <Footer bindingLabel={bindingLabel} />;
  }, [boundListExpr, propertyListItems]);

  return (
    <>
      <HStack
        id="attributes-list-label"
        data-automation-id="attributes-list-label"
        spacing={1}
      >
        <AttributesLabel expr={expr} dispatch={dispatch} />
        <View flex={1}>
          <ExpressionBinding
            automationLabel="Attributes"
            expression={wrapExpr(boundListExpr ?? staticListExpr ?? expr)}
            onChange={onBindingChange}
            bindUIVariant={isShape(shape, 'binding') ? 'dot' : 'label'}
          />
        </View>
        <PropertyListAddButton
          tooltip="Add new attribute"
          aria-label="Add new attribute"
          data-automation-id="ListAddButton"
          onClick={() => {
            // Secondary + externalized: empty loc rows share Map key "" and
            // collapse in the list. Adding another empty opens itemIndex past
            // the last row so the popover never appears — reopen the draft.
            if (
              isLocalizationExternalizedBindingEnabled &&
              !isOnPrimaryLocale
            ) {
              const emptyLoc = propertyListItems.find(
                (item) =>
                  item.source === 'localized' &&
                  hasEmptyAttributeName(stripAll(item.item))
              );
              if (emptyLoc) {
                setItemIndex(emptyLoc.index);
                return;
              }
            }
            onAddItem(ERecord({name: EText(''), value: EText('')}));
          }}
        />
      </HStack>

      <View marginTop="$1" aria-labelledby="attributes-list-label">
        <PropertyList
          sortable={
            !isLocalizationExternalizedBindingEnabled || isOnPrimaryLocale
          }
          disabled={propertyListItems.length === 0}
          deletable={propertyListItems.length > 0}
          onItemDeleted={onItemDeleted}
          onItemDropped={onItemDropped}
          items={propertyListItems}
          renderItem={renderItem}
          renderFooter={renderFooter}
        />
      </View>
    </>
  );
};

export const AttributesType = TypeImplementation.Alias({
  type: TList(
    TTypeConstructor(qualify(pluginName('Basic'), typeName('CustomAttribute')))
  ),
  system: {
    bindUIVariant: 'custom',
  },
  icon: TypeImplementation.IconByName(
    qualify(pluginName('Basic'), iconName('Attributes'))
  ),
  render: AttributesTypeRender,
});
